/**
 * Bero platform console — Society profile. GET /api/platform/societies/:id
 * for detail + stats + admins. SUPER_ADMIN can change the plan (PATCH,
 * optimistic). SUPPORT/SUPER_ADMIN can "enter" the society — POST .../enter,
 * then setActingSociety() + push into /admin so the ordinary society-admin
 * console takes over, with the amber acting banner (Task 6, app/_layout.tsx)
 * visible on every screen from here on until Exit.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import type { PillStatus } from '@bero/tokens';
import { space } from '@bero/tokens';
import { setActingSociety } from '@/lib/acting-society';
import { Icon } from '@/features/account-admin/icons';
import { timeAgo, useFetch } from '@/features/community/lib';
import { usePlatformRole } from '@/features/platform/PlatformGate';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, EmptyState, Hairline, Input, Pill, Screen, SectionLabel, Skeleton, TopBar } from '@/ui';

type SocietyDetail = {
  society: { id: number; name: string; city: string; address: string; plan: string; status: string; createdAt: string | null };
  stats: { flats: number; residents: number; admins: number; lastActivity: string | null };
  admins: { id: number; fullName: string; email: string; phone: string | null; role: string }[];
};

async function fetchSocietyDetail(id: string): Promise<SocietyDetail> {
  return api.get<SocietyDetail>(`/api/platform/societies/${id}`);
}

const ADMIN_ROLE_TONE: Record<string, PillStatus> = { SUB_ADMIN: 'open', MASTER_ADMIN: 'approved' };
const ADMIN_ROLE_LABEL: Record<string, string> = { SUB_ADMIN: 'Sub admin', MASTER_ADMIN: 'Master admin' };

const PLANS = ['trial', 'starter', 'pro'] as const;
const PLAN_LABEL: Record<string, string> = { trial: 'Trial', starter: 'Starter', pro: 'Pro' };

function goBackToSocieties() {
  if (router.canGoBack()) router.back();
  else router.replace('/platform');
}

export default function PlatformSocietyProfileScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors } = useTheme();
  const { t } = useT();
  const role = usePlatformRole();
  const { data, loading, error, refetch, mutate } = useFetch(() => fetchSocietyDetail(String(id)), [id]);

  const [planBusy, setPlanBusy] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [entering, setEntering] = React.useState(false);
  const [enterErr, setEnterErr] = React.useState<string | null>(null);

  const canEnter = role === 'SUPER_ADMIN' || role === 'SUPPORT';
  const canManagePlan = role === 'SUPER_ADMIN';

  const changePlan = async (plan: string) => {
    if (!data || planBusy || plan === data.society.plan) return;
    const prev = data;
    setPlanBusy(true);
    mutate((p) => (p ? { ...p, society: { ...p.society, plan } } : p));
    try {
      await api.patch(`/api/platform/societies/${data.society.id}`, { plan });
    } catch {
      mutate(() => prev);
    } finally {
      setPlanBusy(false);
    }
  };

  const enterSociety = async () => {
    if (!data || entering) return;
    setEntering(true);
    setEnterErr(null);
    try {
      const res = await api.post<{ ok: boolean; society: { id: number; name: string } }>(
        `/api/platform/societies/${data.society.id}/enter`, { note: note.trim() || undefined },
      );
      setActingSociety(res.society.id, res.society.name, role);
      router.push('/admin');
    } catch {
      setEnterErr(t('plat_enter_err'));
      setEntering(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={data?.society.name ?? t('plat_nav_societies')} onBack={goBackToSocieties} brand="none" />
      {loading ? (
        <Screen padded>
          <Skeleton h={80} style={{ marginTop: space.s4, marginBottom: space.s4 }} />
          <Skeleton h={120} style={{ marginBottom: space.s4 }} />
          <Skeleton h={120} />
        </Screen>
      ) : error || !data ? (
        <Screen padded>
          <EmptyState
            icon={<Icon name="building" size={40} color={colors.textTertiary} />}
            title={t('plat_soc_err')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        </Screen>
      ) : (
        <Screen padded fullWidth>
          <View style={{ marginTop: space.s4, marginBottom: space.s5 }}>
            <AppText variant="bodyMd" tone="tertiary">{data.society.city}{data.society.address ? ` · ${data.society.address}` : ''}</AppText>
          </View>

          <SectionLabel>{t('plat_prof_stats')}</SectionLabel>
          <View style={{ flexDirection: 'row', gap: space.s6, marginBottom: space.s5 }}>
            <View>
              <AppText mono variant="headingLg" weight="600">{data.stats.flats}</AppText>
              <AppText variant="bodySm" tone="tertiary">{t('plat_soc_col_flats')}</AppText>
            </View>
            <View>
              <AppText mono variant="headingLg" weight="600">{data.stats.residents}</AppText>
              <AppText variant="bodySm" tone="tertiary">{t('plat_soc_col_residents')}</AppText>
            </View>
            <View>
              <AppText mono variant="headingLg" weight="600">{data.stats.admins}</AppText>
              <AppText variant="bodySm" tone="tertiary">{t('plat_soc_col_admins')}</AppText>
            </View>
            <View>
              <AppText variant="headingSm" weight="500">
                {data.stats.lastActivity ? timeAgo(new Date(data.stats.lastActivity)) : '—'}
              </AppText>
              <AppText variant="bodySm" tone="tertiary">{t('plat_soc_col_activity')}</AppText>
            </View>
          </View>

          <SectionLabel>{t('plat_prof_plan')}</SectionLabel>
          <View style={{ flexDirection: 'row', gap: space.s2, marginBottom: space.s5 }}>
            {PLANS.map((plan) => (
              <Chip
                key={plan}
                label={PLAN_LABEL[plan]}
                active={data.society.plan === plan}
                onPress={canManagePlan ? () => void changePlan(plan) : undefined}
              />
            ))}
          </View>

          <SectionLabel>{t('plat_prof_admins')}</SectionLabel>
          <View style={{ marginBottom: space.s6 }}>
            {data.admins.length === 0 ? (
              <AppText variant="bodySm" tone="tertiary">—</AppText>
            ) : (
              data.admins.map((a, i) => (
                <View key={a.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 12 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500" numberOfLines={1}>{a.fullName}</AppText>
                      <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>
                        {[a.email, a.phone].filter(Boolean).join(' · ')}
                      </AppText>
                    </View>
                    <Pill status={ADMIN_ROLE_TONE[a.role] ?? 'pending'} label={ADMIN_ROLE_LABEL[a.role] ?? a.role} />
                  </View>
                  {i < data.admins.length - 1 ? <Hairline /> : null}
                </View>
              ))
            )}
          </View>

          {canEnter ? (
            <View style={{ gap: space.s3 }}>
              <SectionLabel>{t('plat_prof_enter')}</SectionLabel>
              <Input
                value={note}
                onChangeText={setNote}
                placeholder={t('plat_enter_note_ph')}
                editable={!entering}
              />
              {enterErr ? <AppText variant="bodySm" style={{ color: colors.danger500 }}>{enterErr}</AppText> : null}
              <Button
                title={entering ? t('plat_prof_entering') : t('plat_prof_enter')}
                loading={entering}
                onPress={() => void enterSociety()}
                style={{ alignSelf: 'flex-start' }}
              />
            </View>
          ) : null}
        </Screen>
      )}
    </View>
  );
}
